/* ============================================================================
   kitsters-vanity-url.js - cosmetic address-bar rewrite for a vanity URL.

   WHY THIS EXISTS, and why it is not a redirect.

   Shopify hardcodes the `/pages/` prefix, so the Magic Hour page genuinely
   lives at /pages/magic-hour and nothing theme-side can move it (root
   CLAUDE.md §10.65 is the same fact seen from /policies/*). An Admin URL
   redirect solves the INBOUND direction only - /magic-hour ->
   /pages/magic-hour, created by Kitsters-USA/scripts/us-vanity-redirect.py -
   and it cannot be pointed the other way:

     - Shopify gives an existing resource priority over any redirect on its
       path, so a redirect on /pages/magic-hour is accepted, appears in Admin,
       and never fires. Measured rather than quoted (2026-08-29): the India
       store has had a record /collections/all -> /collections/shop-all-diy-kits
       for months and https://kitsters.in/collections/all still answers 200.
       That is the "inert rather than poisonous" shape - it would read as a
       working feature forever.
     - And if it did fire it would point at /magic-hour, which points back
       here: an infinite loop that takes the page offline.

   So the address bar is rewritten client-side. The page is served normally at
   /pages/magic-hour and only the DISPLAYED url changes.

   ── THE RUNTIME GUARD, which is why this file exists again at all ──
   This store shipped this rewrite once WITHOUT the redirect record and put a
   404 in every visitor's address bar (owner report 2026-08-28, PR #1185 deleted
   it). A comment is not a safeguard, so the check is executed: HEAD the short
   url and rewrite only if it actually resolves. Consequences, all deliberate:
     - Before the record exists this file does nothing at all. No 404 is
       reachable, in either landing order.
     - The moment the record is created the rewrite starts working on its own,
       with no deploy.
     - One same-origin HEAD per hub visit, after load. It is cached by the
       browser and it blocks nothing.
   `fetch` and `AbortController` are both assumed; anything without them simply
   skips the rewrite, which is the safe direction.

   WHAT THIS DELIBERATELY DOES NOT CHANGE
   - The canonical stays /pages/magic-hour. That is the real url and the correct
     canonical; do not "fix" it to match the bar.
   - Analytics still attributes the hit to /pages/magic-hour, because every tag
     fires on load, before this runs. Accepted knowingly. Do not "fix" it by
     re-firing a pageview; that double-counts.

   SAFETY
   - replaceState, never pushState: no history entry is added, so Back still
     goes to the previous page rather than trapping the shopper here.
   - Once the record exists a refresh re-requests /magic-hour, which 301s back
     here and re-runs this, so the short url is stable across reloads.
   ========================================================================= */
(function () {
  'use strict';

  var tag = document.querySelector('script[data-kt-vanity]');
  if (!tag) return;

  var vanity = tag.getAttribute('data-kt-vanity');
  var real = tag.getAttribute('data-kt-real');
  if (!vanity || !real) return;

  if (!window.history || typeof window.history.replaceState !== 'function') return;
  if (typeof window.fetch !== 'function') return;

  /* Belt: the Liquid gate already guarantees this, but a cached or rewritten
     document could disagree. Never rewrite from a path we were not told about. */
  if (window.location.pathname !== real) return;

  function rewrite() {
    try {
      window.history.replaceState(
        window.history.state,
        '',
        vanity + window.location.search + window.location.hash
      );
    } catch (e) {
      /* Sandboxed or cross-origin document - leave the url exactly as it is.
         The page is fully functional either way; this is cosmetic only. */
    }
  }

  /* Does the short url actually resolve? `redirect: 'follow'` is the point:
     with the Admin record in place this follows the 301 and lands on a 200, so
     `res.ok` is the real answer to "can a shopper paste this and get the page".
     Any failure - 404, network, CORS, an exception - leaves the bar alone. */
  function verifyThenRewrite() {
    var done = false;
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = window.setTimeout(function () {
      if (!done && ctrl) ctrl.abort();
    }, 4000);

    var opts = { method: 'HEAD', redirect: 'follow', credentials: 'same-origin' };
    if (ctrl) opts.signal = ctrl.signal;

    window.fetch(vanity, opts).then(function (res) {
      done = true;
      window.clearTimeout(timer);
      if (res && res.ok) rewrite();
    })['catch'](function () {
      done = true;
      window.clearTimeout(timer);
    });
  }

  /* After load: this is cosmetic and must never compete with the page's own
     requests. `requestIdleCallback` where available, a timeout otherwise. */
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(verifyThenRewrite, { timeout: 3000 });
  } else {
    window.setTimeout(verifyThenRewrite, 600);
  }
})();
